import { supabase } from "@/integrations/supabase/client";
import { plentyOneApi } from "@/services/plentyOneApi";
import { hashPassword, verifyPassword, validatePassword } from "./passwordUtils";
import { passwordResetService } from "./passwordResetService";
import { logger } from "@/services/logger";

interface B2BAuthUser {
  id: string;
  email: string;
  erpContactId?: number;
  isActive: boolean;
  createdAt: string;
  lastLogin?: string;
  pricingType?: string;
  paymentMethodType?: string;
}

interface LoginResult {
  success: boolean;
  user?: B2BAuthUser;
  erpContact?: unknown;
  error?: string;
  requiresPasswordChange?: boolean;
}

interface RegisterResult {
  success: boolean;
  user?: B2BAuthUser;
  error?: string;
  validationErrors?: string[];
}

class B2BAuthService {
  private readonly MAX_LOGIN_ATTEMPTS = 5;
  private readonly LOCK_DURATION_MINUTES = 30;

  /**
   * B2B顧客のログイン
   */
  async login(email: string, password: string): Promise<LoginResult> {
    logger.info('[B2BAuthService] Login attempt started');
    
    try {
      const normalizedEmail = email.toLowerCase().trim();

      // 1. B2B認証テーブルからユーザー情報を取得
      logger.debug('[B2BAuthService] Fetching user from b2b_auth table');
      const { data: authUser, error: authError } = await supabase
        .from('b2b_auth')
        .select('*')
        .eq('email', normalizedEmail)
        .single();

      if (authError || !authUser) {
        logger.error('[B2BAuthService] User lookup failed');
        // ユーザーが存在しない場合、セキュリティのため詳細なエラーは返さない
        return {
          success: false,
          error: 'Invalid email or password'
        };
      }
      
      logger.debug('[B2BAuthService] User found', {
        isActive: authUser.is_active,
        hasErpContact: !!authUser.erp_contact_id,
        isLocked: !!authUser.locked_until
      });

      // 2. アカウントロックチェック
      if (authUser.locked_until) {
        const lockTime = new Date(authUser.locked_until);
        if (lockTime > new Date()) {
          logger.warn('[B2BAuthService] Account is locked');
          return {
            success: false,
            error: `Account is locked. Please try again after ${lockTime.toLocaleString()}`
          };
        }
      }

      // 3. アカウントアクティブチェック
      if (!authUser.is_active) {
        logger.warn('[B2BAuthService] Account is inactive');
        return {
          success: false,
          error: 'Account is inactive. Please contact support.'
        };
      }

      // 4. パスワード検証
      logger.debug('[B2BAuthService] Verifying password');
      const isPasswordValid = await verifyPassword(password, authUser.password_hash);
      
      if (!isPasswordValid) {
        logger.warn('[B2BAuthService] Invalid password attempt');
        // ログイン失敗回数を増やす
        const failedAttempts = (authUser.failed_login_attempts || 0) + 1;
        
        if (failedAttempts >= this.MAX_LOGIN_ATTEMPTS) {
          // アカウントをロック
          const lockUntil = new Date();
          lockUntil.setMinutes(lockUntil.getMinutes() + this.LOCK_DURATION_MINUTES);
          
          await supabase
            .from('b2b_auth')
            .update({
              failed_login_attempts: failedAttempts,
              locked_until: lockUntil.toISOString()
            })
            .eq('id', authUser.id);
            
          return {
            success: false,
            error: `Too many failed attempts. Account locked for ${this.LOCK_DURATION_MINUTES} minutes.`
          };
        } else {
          // 失敗回数を更新
          await supabase
            .from('b2b_auth')
            .update({ failed_login_attempts: failedAttempts })
            .eq('id', authUser.id);
            
          return {
            success: false,
            error: 'Invalid email or password'
          };
        }
      }

      // 5. ログイン成功 - 失敗回数をリセット、最終ログイン時刻を更新
      logger.debug('[B2BAuthService] Password valid, updating login info');
      const { error: updateError } = await supabase
        .from('b2b_auth')
        .update({
          failed_login_attempts: 0,
          locked_until: null,
          last_login: new Date().toISOString()
        })
        .eq('id', authUser.id);
        
      if (updateError) {
        logger.error('[B2BAuthService] Failed to update login info', { error: updateError.message });
      }

      // 5.1. 行動ログを記録 (エラーはログイン処理を妨げない)
      const { logActivity } = await import('@/services/activityLogger');
      (async () => {
        try {
          await logActivity({
            userId: authUser.id,
            actionType: 'USER_LOGIN',
            details: { source: 'WebApp' }
          });
        } catch (e) {
          logger.error('[B2BAuthService] Exception while logging user activity', { error: e instanceof Error ? e.message : String(e) });
        }
      })();

      // 6. ERPコンタクト情報を取得（あれば）
      let erpContact = null;
      if (authUser.erp_contact_id) {
        try {
          logger.debug('[B2BAuthService] Fetching ERP contact');
          erpContact = await plentyOneApi.getContactInfo(authUser.erp_contact_id);
          logger.debug('[B2BAuthService] ERP contact fetched successfully');
        } catch (error) {
          logger.error('[B2BAuthService] Failed to fetch ERP contact info', {
            error: error instanceof Error ? error.message : String(error)
          });
          // ERPエラーはログインを妨げない
        }
      }

      const user: B2BAuthUser = {
        id: authUser.id,
        email: authUser.email,
        erpContactId: authUser.erp_contact_id,
        isActive: authUser.is_active,
        createdAt: authUser.created_at,
        lastLogin: authUser.last_login,
        pricingType: authUser.pricing_type,
        paymentMethodType: authUser.payment_method_type
      };

      logger.info('[B2BAuthService] Login successful', {
        hasErpContact: !!erpContact,
        requiresPasswordChange: authUser.password_reset_required || false
      });

      return {
        success: true,
        user,
        erpContact,
        requiresPasswordChange: authUser.password_reset_required || false
      };
    } catch (error) {
      logger.error('[B2BAuthService] Unexpected login error', {
        error: error instanceof Error ? error.message : String(error)
      });
      return {
        success: false,
        error: 'An unexpected error occurred during login'
      };
    }
  }

  /**
   * 新規B2B顧客の登録
   */
  async register(email: string, password: string, erpContactId?: number, language?: string): Promise<RegisterResult> {
    try {
      const normalizedEmail = email.toLowerCase().trim();

      // 1. パスワードバリデーション
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.valid) {
        return {
          success: false,
          error: 'Password does not meet requirements',
          validationErrors: passwordValidation.errors
        };
      }

      // 2. 既存ユーザーチェック
      const { data: existingUsers, error: checkError } = await supabase
        .from('b2b_auth')
        .select('id')
        .eq('email', normalizedEmail);

      if (checkError) {
        logger.error('Error checking existing user', { error: checkError.message });
        return {
          success: false,
          error: `Database error: ${checkError.message}`
        };
      }

      if (existingUsers && existingUsers.length > 0) {
        return {
          success: false,
          error: 'An account with this email already exists'
        };
      }

      // 3. ERPコンタクトの確認（必須）- B2B顧客のみ登録可能
      let contactId = erpContactId;
      let pricingType: string | undefined;
      let paymentMethodType: string | undefined;
      
      if (!contactId) {
        try {
          const erpContact = await plentyOneApi.findContactByEmail(normalizedEmail);
          if (erpContact) {
            contactId = erpContact.id;
            // ERPクラスIDから導出された価格タイプと支払いタイプを取得
            pricingType = erpContact.pricing_type;
            paymentMethodType = erpContact.payment_method_type;
          }
        } catch (error) {
          logger.error('ERP contact search failed', { error: error instanceof Error ? error.message : String(error) });
          return {
            success: false,
            error: 'Unable to verify B2B customer status. Please contact support.'
          };
        }
      }

      // ERPコンタクトが見つからない場合は登録を拒否
      if (!contactId) {
        return {
          success: false,
          error: 'This email is not registered as a B2B customer. Please contact sales to establish a business relationship first.'
        };
      }

      // 4. パスワードハッシュ化
      const passwordHash = await hashPassword(password);

      // 5. ユーザー作成
      const { data: newUser, error: createError } = await supabase
        .from('b2b_auth')
        .insert({
          email: normalizedEmail,
          password_hash: passwordHash,
          erp_contact_id: contactId,
          is_active: true,
          pricing_type: pricingType,
          payment_method_type: paymentMethodType
        })
        .select()
        .single();

      if (createError || !newUser) {
        logger.error('User creation failed', { error: createError?.message });
        return {
          success: false,
          error: `Failed to create account: ${createError?.message || 'Unknown error'}`
        };
      }

      // Log user registration activity
      const { logActivity: logRegistration } = await import('@/services/activityLogger');
      try {
        await logRegistration({
          userId: newUser.id,
          actionType: 'USER_REGISTRATION',
          details: { source: 'WebApp' }
        });
      } catch (logError) {
        logger.error('[B2BAuthService] Failed to log user registration', {
          error: logError instanceof Error ? logError.message : String(logError)
        });
      }

      const user: B2BAuthUser = {
        id: newUser.id,
        email: newUser.email,
        erpContactId: newUser.erp_contact_id,
        isActive: newUser.is_active,
        createdAt: newUser.created_at,
        pricingType: newUser.pricing_type,
        paymentMethodType: newUser.payment_method_type
      };

      // Send welcome email
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        const functionUrl = `${supabaseUrl}/functions/v1/send-email`;
        
        logger.info('Attempting to send welcome email', { 
          email: newUser.email,
          functionUrl,
          hasAnonKey: !!anonKey,
          language: language || 'de'
        });
        
        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${anonKey}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'welcome',
            to: newUser.email,
            data: {
              email: newUser.email,
              language: (language === 'en' ? 'en' : 'de') as 'de' | 'en',
              loginUrl: `${window.location.origin}/login`,
              resetPasswordUrl: `${window.location.origin}/request-password-reset`
            }
          })
        });
        
        const responseText = await response.text();
        logger.info('Welcome email response', { 
          status: response.status, 
          statusText: response.statusText,
          responseText 
        });
        
        if (!response.ok) {
          logger.warn('Failed to send welcome email', { 
            userId: newUser.id, 
            status: response.status,
            responseText 
          });
          // Don't fail registration if email fails
        } else {
          logger.info('Welcome email sent successfully', { userId: newUser.id });
        }
      } catch (error) {
        logger.error('Error sending welcome email', { 
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined
        });
        // Don't fail registration if email fails
      }

      return {
        success: true,
        user
      };
    } catch (error) {
      logger.error('Registration error', { error: error instanceof Error ? error.message : String(error) });
      return {
        success: false,
        error: 'An unexpected error occurred during registration'
      };
    }
  }

  /**
   * パスワードリセットリクエスト
   */
  async requestPasswordReset(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      const normalizedEmail = email.toLowerCase().trim();

      // ユーザー存在確認
      const { data: user } = await supabase
        .from('b2b_auth')
        .select('id, is_active')
        .eq('email', normalizedEmail)
        .single();

      if (!user || !user.is_active) {
        // セキュリティのため、ユーザーが存在しない場合も成功を返す
        return { success: true };
      }

      // Log password reset request activity
      try {
        await supabase.from('user_activity_logs').insert({
          user_id: user.id,
          action_type: 'PASSWORD_RESET_REQUESTED',
          details: { source: 'WebApp' }
        });
      } catch (logError) {
        logger.error('[B2BAuthService] Failed to log password reset request', { 
          error: logError instanceof Error ? logError.message : String(logError) 
        });
      }

      // TODO: パスワードリセットメール送信ロジック
      // ここでは実装の枠組みのみ示す
      logger.info('Password reset requested');

      return { success: true };
    } catch (error) {
      logger.error('Password reset request error', { error: error instanceof Error ? error.message : String(error) });
      return {
        success: false,
        error: 'Failed to process password reset request'
      };
    }
  }

  /**
   * パスワード変更
   */
  async changePassword(userId: string, currentPassword: string, newPassword: string): Promise<{ success: boolean; error?: string; validationErrors?: string[] }> {
    try {
      // 1. ユーザー情報取得
      const { data: user, error: fetchError } = await supabase
        .from('b2b_auth')
        .select('password_hash')
        .eq('id', userId)
        .single();

      if (fetchError || !user) {
        return {
          success: false,
          error: 'User not found'
        };
      }

      // 2. 現在のパスワード検証
      const isCurrentPasswordValid = await verifyPassword(currentPassword, user.password_hash);
      if (!isCurrentPasswordValid) {
        return {
          success: false,
          error: 'Current password is incorrect'
        };
      }

      // 3. 新しいパスワードバリデーション
      const passwordValidation = validatePassword(newPassword);
      if (!passwordValidation.valid) {
        return {
          success: false,
          error: 'New password does not meet requirements',
          validationErrors: passwordValidation.errors
        };
      }

      // 4. 新しいパスワードハッシュ化
      const newPasswordHash = await hashPassword(newPassword);

      // 5. パスワード更新
      const { error: updateError } = await supabase
        .from('b2b_auth')
        .update({ password_hash: newPasswordHash })
        .eq('id', userId);

      if (updateError) {
        logger.error('Password update error', { error: updateError.message });
        return {
          success: false,
          error: 'Failed to update password'
        };
      }

      // Log password change activity
      const { logActivity: logPasswordChange } = await import('@/services/activityLogger');
      try {
        await logPasswordChange({
          userId: userId,
          actionType: 'PASSWORD_RESET_COMPLETED',
          details: { source: 'WebApp', context: 'User-initiated password change' }
        });
      } catch (logError) {
        logger.error('[B2BAuthService] Failed to log password change', {
          error: logError instanceof Error ? logError.message : String(logError)
        });
      }

      return { success: true };
    } catch (error) {
      logger.error('Change password error', { error: error instanceof Error ? error.message : String(error) });
      return {
        success: false,
        error: 'An unexpected error occurred while changing password'
      };
    }
  }

  /**
   * ERPコンタクトとのリンク
   */
  async linkErpContact(userId: string, erpContactId: number): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('b2b_auth')
        .update({ erp_contact_id: erpContactId })
        .eq('id', userId);

      if (error) {
        logger.error('ERP link error', { error: error.message });
        return {
          success: false,
          error: 'Failed to link ERP contact'
        };
      }

      return { success: true };
    } catch (error) {
      logger.error('Link ERP contact error', { error: error instanceof Error ? error.message : String(error) });
      return {
        success: false,
        error: 'An unexpected error occurred while linking ERP contact'
      };
    }
  }
}

export const b2bAuthService = new B2BAuthService();